import React, { useState, useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MessageSquarePlus, X, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

const PRIORITIES = [
  "P0 - Blocking (can't work)",
  "P1 - Broken but can work around",
  "P2 – annoying or slow",
  "P3 – idea",
];

export function helperText(pathname, caseId, typed) {
  const page = pathname.startsWith("/cases/") ? "a case page" : pathname.replace("/", "") || "home";
  return `RM Desk help: I'm on ${page}${caseId ? ` for ${caseId}` : ""}. ${(typed || "").trim()}`.trim();
}
export async function askHelper(text) {
  try { await navigator.clipboard.writeText(text); toast.success("Copied — paste it in Claude"); } catch { /* clipboard blocked */ }
  window.open(`https://claude.ai/new?q=${encodeURIComponent(text)}`, "_blank", "noopener");
}

export function FeedbackWidget({ caseHumanId, hideFab = false }) {
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState("");
  const [details, setDetails] = useState("");
  const [priority, setPriority] = useState("P2 – annoying or slow");
  const [withShot, setWithShot] = useState(false);
  const [images, setImages] = useState([]);
  useEffect(() => { const h = () => setOpen(true); window.addEventListener("truva:feedback", h); return () => window.removeEventListener("truva:feedback", h); }, []);
  const addFiles = (files) => {
    [...files].filter((f) => f.type.startsWith("image/")).slice(0, 5 - images.length).forEach((f) => {
      const rd = new FileReader(); rd.onload = () => setImages((im) => [...im, { name: f.name, data: rd.result }]); rd.readAsDataURL(f);
    });
  };
  const onPaste = (e) => { const fs = [...(e.clipboardData?.items || [])].filter((i) => i.kind === "file").map((i) => i.getAsFile()); if (fs.length) { e.preventDefault(); addFiles(fs); } };
  const [saving, setSaving] = useState(false);
  const loc = useLocation();
  const params = useParams();

  const caseId = loc.pathname.includes("/cases/") ? (params.recordId || "") : "";

  const captureShot = async () => {
    try {
      const mod = await import("html2canvas");
      const canvas = await mod.default(document.body, { logging: false, useCORS: true, scale: 0.6 });
      return canvas.toDataURL("image/png");
    } catch {
      return null;
    }
  };

  const submit = async () => {
    if (!summary.trim()) { toast.error("Summary is required"); return; }
    setSaving(true);
    try {
      let shot = null;
      if (withShot) shot = await captureShot();
      await api.post("/feedback", {
        summary, details, priority,
        page: loc.pathname + loc.search,
        case_id: caseId,
        browser: navigator.userAgent,
        screenshot_base64: shot,
        attachments_base64: images.map((i) => i.data),
      });
      toast.success("Thanks! Feedback sent.");
      setSummary(""); setDetails(""); setPriority("P2 – annoying or slow"); setWithShot(false); setImages([]);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send feedback");
    } finally { setSaving(false); }
  };

  return (
    <>
      {!hideFab && <button onClick={() => setOpen(true)} data-testid="feedback-fab"
        className="fixed bottom-4 right-4 z-[60] flex items-center gap-1.5 bg-[#FF4802] text-white px-3 py-1.5 rounded-full shadow-md hover:bg-[#E2521B] transition-colors font-medium text-xs">
        <MessageSquarePlus className="w-3.5 h-3.5" /> Feedback
      </button>}

      {open && (
        <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-end sm:justify-center bg-black/30" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full sm:max-w-md m-4 p-6" onClick={(e) => e.stopPropagation()} onPaste={onPaste} data-testid="feedback-panel">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-[#333333]">Send feedback</h3>
              <button onClick={() => setOpen(false)} className="text-[#6B7280] hover:text-[#333333]"><X className="w-5 h-5" /></button>
            </div>
            <div className="rounded-xl border border-[#38A2FF]/30 bg-[#38A2FF]/5 p-3 mb-3 flex items-center justify-between gap-3" data-testid="feedback-helper-card">
              <span className="text-xs text-[#1e7fd6]">Not sure if it's a bug? Ask the RM Desk helper first.</span>
              <button onClick={() => askHelper(helperText(loc.pathname, caseHumanId || window.__truvaCaseId || "", `${summary} ${details}`))} data-testid="feedback-ask-claude"
                className="text-xs bg-[#38A2FF] text-white rounded-lg px-3 py-1.5 font-medium whitespace-nowrap">Ask in Claude</button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-[#6B7280] mb-1.5">Summary *</label>
                <Input value={summary} onChange={(e) => setSummary(e.target.value)} data-testid="feedback-summary" placeholder="One line: what happened?" className="border-[#E2E2E2] rounded-lg" />
              </div>
              <div>
                <label className="block text-xs text-[#6B7280] mb-1.5">Details</label>
                <Textarea value={details} onChange={(e) => setDetails(e.target.value)} data-testid="feedback-details" placeholder="Steps, what you expected, etc." className="border-[#E2E2E2] rounded-lg" />
              </div>
              <div>
                <label className="block text-xs text-[#6B7280] mb-1.5">Priority</label>
                <Select value={priority} onValueChange={setPriority}>
                  <SelectTrigger data-testid="feedback-priority" className="border-[#E2E2E2] rounded-lg"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white rounded-xl border-[#E2E2E2]">
                    {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <label className="flex items-center gap-2 text-sm text-[#333333] cursor-pointer">
                <input type="checkbox" className="accent-[#FF4802] w-4 h-4" checked={withShot} onChange={(e) => setWithShot(e.target.checked)} data-testid="feedback-screenshot" />
                Attach a screenshot of this screen
              </label>
              <div data-testid="feedback-attachments">
                <label className="flex items-center gap-2 text-xs text-[#6B7280] cursor-pointer">
                  <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="feedback-image-input" />
                  <span className="border border-dashed border-[#E2E2E2] rounded-lg px-3 py-2 w-full text-center hover:border-[#FF4802]">Add images — click to choose, or paste a screenshot here (max 5)</span>
                </label>
                {images.length > 0 && (
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {images.map((im, i) => (
                      <div key={i} className="relative" data-testid={`feedback-image-${i}`}>
                        <img src={im.data} alt={im.name} className="w-16 h-16 object-cover rounded-lg border border-[#E2E2E2]" />
                        <button onClick={() => setImages(images.filter((_, j) => j !== i))} className="absolute -top-1.5 -right-1.5 bg-white border border-[#E2E2E2] rounded-full w-5 h-5 text-[10px] text-[#CA1212]">✕</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <button onClick={submit} disabled={saving} data-testid="feedback-submit"
              className="mt-4 w-full flex items-center justify-center gap-2 bg-[#FF4802] text-white py-2.5 rounded-lg hover:bg-[#E2521B] disabled:opacity-50 font-medium text-sm">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Send feedback
            </button>
          </div>
        </div>
      )}
    </>
  );
}
